import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  AdditionalServiceLevelResponse,
  AditionalService,
} from 'src/app/pages/orders/interfaces/additionalService.interface';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { GLOBAL_HEADERS } from 'src/app/store/store.constants';
import { Store } from '@ngrx/store';
import { AppState } from '../reducers';
import { loadAdditionalServiceLevelSuccessByKey } from './additional-services.actions';
import {
  selectAdditionalServiceLevelByKey,
  selectAdditionalServiceLevelHasDataByKey,
} from './additional-services.selectors';
import { catchError, filter, finalize, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbOtCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { fromIdbOtCatalog } from 'src/app/idb/idb-rxjs.helpers';

@Injectable({ providedIn: 'root' })
export class AdditionalServicesApiService {
  private api = environment.APIM + environment.newOrderAPI + 'AdditionalService';
  private pending: { [key: string]: Observable<AdditionalServiceLevelResponse> } = {};

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private idb: ManageIdbService,
  ) {}

  getServicesLevel(plantId: number = null, contractCode: string = null): Observable<AdditionalServiceLevelResponse> {
    const key = this.buildKey(plantId, contractCode);
    const isNoParams = plantId === null && !contractCode;

    this.store
      .select(selectAdditionalServiceLevelHasDataByKey(key))
      .pipe(take(1))
      .subscribe((has) => {
        if (!has && !this.pending[key]) {
          if (isNoParams) {
            this.pending[key] = this.hydrateFromIdbOrBackend(key).pipe(shareReplay(1), take(1));
            this.pending[key].subscribe();
          } else {
            const url = this.buildUrl(plantId, contractCode);
            this.pending[key] = this.http.get<AdditionalServiceLevelResponse>(url, GLOBAL_HEADERS).pipe(
              take(1),
              tap((response) => {
                this.store.dispatch(loadAdditionalServiceLevelSuccessByKey({ key, response }));
              }),
              catchError(() => {
                const fallback: AdditionalServiceLevelResponse = {
                  value: [],
                  formatters: [],
                  contentTypes: [],
                  declaredType: null,
                  statusCode: 0,
                };
                this.store.dispatch(loadAdditionalServiceLevelSuccessByKey({ key, response: fallback }));
                return of(fallback);
              }),
              shareReplay(1),
              finalize(() => {
                delete this.pending[key];
              }),
            );
            this.pending[key].subscribe();
          }
        }
      });

    return this.store.select(selectAdditionalServiceLevelByKey(key)).pipe(
      filter((resp) => !!resp),
      take(1),
    ) as unknown as Observable<AdditionalServiceLevelResponse>;
  }

  private hydrateFromIdbOrBackend(key: string): Observable<AdditionalServiceLevelResponse> {
    return fromIdbOtCatalog(this.idb, IdbOtCatalogName.AdditionalServices).pipe(
      switchMap((idbData: AditionalService[] | null) => {
        if (idbData?.length) {
          const response: AdditionalServiceLevelResponse = {
            value: idbData,
            formatters: [],
            contentTypes: [],
            declaredType: null,
            statusCode: 200,
          };
          this.store.dispatch(loadAdditionalServiceLevelSuccessByKey({ key, response }));
          return of(response);
        }
        return this.fetchFromBackendNoParams(key);
      }),
      catchError(() => this.fetchFromBackendNoParams(key)),
      finalize(() => {
        delete this.pending[key];
      }),
    );
  }

  private fetchFromBackendNoParams(key: string): Observable<AdditionalServiceLevelResponse> {
    return this.http.get<AdditionalServiceLevelResponse>(this.api, GLOBAL_HEADERS).pipe(
      take(1),
      tap((response) => {
        this.store.dispatch(loadAdditionalServiceLevelSuccessByKey({ key, response }));
        if (response?.value) {
          this.idb.saveOtCatalog(IdbOtCatalogName.AdditionalServices, response.value).catch(() => undefined);
        }
      }),
      catchError(() => {
        const fallback: AdditionalServiceLevelResponse = {
          value: [],
          formatters: [],
          contentTypes: [],
          declaredType: null,
          statusCode: 0,
        };
        this.store.dispatch(loadAdditionalServiceLevelSuccessByKey({ key, response: fallback }));
        return of(fallback);
      }),
    );
  }

  private buildUrl(plantId: number = null, contractCode: string = null): string {
    let url = this.api;
    const params: string[] = [];
    if (plantId !== null && plantId !== undefined) {
      params.push(`PlantId=${plantId}`);
    }
    if (contractCode) {
      params.push(`ContractCode=${contractCode}`);
    }
    if (params.length > 0) {
      url += '?' + params.join('&');
    }
    return url;
  }

  private buildKey(plantId: number = null, contractCode: string = null): string {
    const p = plantId === null || plantId === undefined ? '' : this.normalizeValue(String(plantId));
    const c = this.normalizeString(contractCode);
    return `plantId=${p}|contractCode=${c}`;
  }

  private normalizeValue(val: string): string {
    const v = (val || '').trim();
    const isNum = /^-?\d+(\.\d+)?$/.test(v);
    return isNum ? String(Number(v)) : v;
  }

  private normalizeString(val: string | null | undefined): string {
    if (!val) return '';
    return String(val).trim();
  }
}
